/**
 * The editor's right-hand column: what is wrong (errors, warnings, conventions), how the two
 * reference rooms fare, the translation state per locale with the loop that fills it, who may run
 * the scenario, and deleting it. Findings are buttons that take the author to the field at fault.
 */
import { LOCALES } from "@shared/i18n";
import {
  PLAYTHROUGH_THRESHOLDS,
  type PlaythroughSummary,
  type ScenarioMeta,
  type ValidationIssue,
  type Visibility,
} from "@shared/scenario";
import { useRef, useState } from "preact/hooks";
import { Icon } from "../../components/Icon";
import { Button, CopyButton, Dialog, Segmented } from "../../components/ui";
import { useT } from "../../i18n";
import { type ApiError, del, post } from "../../lib/api";

export interface TranslationStatus {
  coverage: number;
  missing: number;
  outdated: number;
  stale: number;
  updatedAt?: number;
}

interface TranslateStep {
  locale: string;
  model: string;
  coverage: number;
  remaining: number;
  accepted: number;
  rejected: unknown[];
  failures: { batch: string; error: string }[];
  status: TranslationStatus | null;
}

export interface Checks {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  conventions: ValidationIssue[];
  play: PlaythroughSummary | null;
}

function FindingGroup({
  title,
  level,
  issues,
  onJump,
  emptyText,
}: {
  title: string;
  level: "error" | "warning" | "convention";
  issues: ValidationIssue[];
  onJump: (path: string) => void;
  emptyText: string;
}) {
  const icon = level === "error" ? "octagon-alert" : level === "warning" ? "alert-triangle" : "info";
  const [open, setOpen] = useState(true);
  const id = `findings-${level}`;
  return (
    <div class={`build-findings ${level}`}>
      <button
        type="button"
        class="build-findings-head"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <Icon name={icon} class="build-level" />
        <span class="build-findings-title">{title}</span>
        <span class={`build-count ${issues.length ? level : ""}`}>{issues.length}</span>
        <Icon name="chevron-down" class={open ? "" : "build-rot"} />
      </button>
      {open ? (
        issues.length ? (
          <ul id={id} class="build-finding-list">
            {issues.map((i, n) => (
              <li key={n}>
                <button type="button" class="build-finding" onClick={() => onJump(i.path)}>
                  <span class="build-finding-path">{i.path || "—"}</span>
                  <span class="build-finding-msg">{i.message}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p id={id} class="muted small build-findings-empty">
            {emptyText}
          </p>
        )
      ) : null}
    </div>
  );
}

function Playthrough({ play }: { play: PlaythroughSummary | null }) {
  const t = useT();
  if (!play) return <p class="muted small">{t("build.play.unavailable")}</p>;
  const th = PLAYTHROUGH_THRESHOLDS;
  const bestOk = play.best.finished && (play.best.readiness ?? 0) >= th.bestReadinessMin;
  const worstOk = play.worst.finished && (play.worst.readiness ?? 100) <= th.worstReadinessMax;
  const decisionsOk = play.best.decisions >= th.minDecisions;
  const row = (ok: boolean, label: string, value: string, target: string) => (
    <li class={`build-play-row ${ok ? "ok" : "off"}`}>
      <Icon name={ok ? "circle-check" : "alert-triangle"} />
      <span class="build-play-label">{label}</span>
      <span class="build-play-value mono">{value}</span>
      <span class="build-play-target muted xs">{target}</span>
    </li>
  );
  return (
    <ul class="build-play">
      {row(
        bestOk,
        t("build.play.best"),
        play.best.finished ? String(play.best.readiness ?? 0) : t("build.play.stuck"),
        t("build.play.atLeast", { n: th.bestReadinessMin }),
      )}
      {row(
        worstOk,
        t("build.play.worst"),
        play.worst.finished ? String(play.worst.readiness ?? 0) : t("build.play.stuck"),
        t("build.play.atMost", { n: th.worstReadinessMax }),
      )}
      {row(
        decisionsOk,
        t("build.play.decisions"),
        String(play.best.decisions),
        t("build.play.atLeast", { n: th.minDecisions }),
      )}
      {play.best.unexercised.length ? (
        <li class="build-play-row off">
          <Icon name="alert-triangle" />
          <span class="build-play-label">{t("build.play.unexercised")}</span>
          <span class="build-play-value mono build-wrap">{play.best.unexercised.join(", ")}</span>
        </li>
      ) : (
        <li class="build-play-row ok">
          <Icon name="circle-check" />
          <span class="build-play-label">{t("build.play.allExercised")}</span>
        </li>
      )}
    </ul>
  );
}

interface LocaleRun {
  running: boolean;
  error: string | null;
}

function Translations({
  id,
  statuses,
  totalStrings,
  model,
  ensureSaved,
  onStatus,
}: {
  id: string;
  statuses: Record<string, TranslationStatus>;
  /** Translatable strings in the working copy; a locale with no file yet is missing all of them. */
  totalStrings: number;
  model: string | null;
  ensureSaved: () => Promise<boolean>;
  onStatus: (locale: string, status: TranslationStatus | null) => void;
}) {
  const t = useT();
  const [runs, setRuns] = useState<Record<string, LocaleRun>>({});
  const [unavailable, setUnavailable] = useState(model === null);
  const [total, setTotal] = useState(0);
  const [batch, setBatch] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const stop = useRef(false);
  const locales = LOCALES.filter((l) => l.tag !== "en");
  const busy = batch || Object.values(runs).some((r) => r.running);

  const statusOf = (tag: string): TranslationStatus =>
    statuses[tag] ?? { coverage: 0, missing: totalStrings, outdated: 0, stale: 0 };
  const current = (s: TranslationStatus) => s.missing === 0 && s.outdated === 0 && s.coverage >= 0.999;

  /** One locale to completion: call, merge, repeat while strings remain and progress is made. */
  const runLocale = async (tag: string): Promise<boolean> => {
    setRuns((r) => ({ ...r, [tag]: { running: true, error: null } }));
    try {
      for (let guard = 0; guard < 200; guard++) {
        if (stop.current) break;
        const res = await post<TranslateStep>(`/api/scenarios/${id}/translate`, {
          locale: tag,
          maxBatches: 2,
        });
        onStatus(tag, res.status);
        setTotal((n) => n + res.accepted);
        if (res.remaining === 0) break;
        if (res.accepted === 0) {
          const why =
            res.failures[0]?.error ?? t("build.translate.noProgress", { count: res.rejected.length });
          throw new Error(why);
        }
      }
      setRuns((r) => ({ ...r, [tag]: { running: false, error: null } }));
      return true;
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 503 || err.code === "translationUnavailable") setUnavailable(true);
      const message =
        err.code === "translationUnavailable"
          ? t("build.translate.unavailable")
          : err.code === "rateLimited"
            ? t("build.translate.rateLimited")
            : err.message || t("error.generic");
      setRuns((r) => ({ ...r, [tag]: { running: false, error: message } }));
      return false;
    }
  };

  const start = async (tags: string[]) => {
    stop.current = false;
    setTotal(0);
    setBatch(tags.length > 1);
    if (!(await ensureSaved())) {
      setBatch(false);
      setRuns((r) => ({ ...r, [tags[0] ?? ""]: { running: false, error: t("build.translate.saveFirst") } }));
      return;
    }
    for (const tag of tags) {
      if (stop.current) break;
      if (!(await runLocale(tag))) break;
    }
    setBatch(false);
  };

  const remove = async (tag: string) => {
    setRemoving(null);
    try {
      await del(`/api/scenarios/${id}/translations/${encodeURIComponent(tag)}`);
      onStatus(tag, null);
    } catch (e) {
      setRuns((r) => ({ ...r, [tag]: { running: false, error: (e as ApiError).message } }));
    }
  };

  const pending = locales.filter((l) => !current(statusOf(l.tag))).map((l) => l.tag);
  return (
    <div class="stack-sm">
      <p class="muted small">
        {unavailable ? t("build.translate.unavailable") : t("build.translate.model", { model: model ?? "—" })}
      </p>
      <div class="row">
        {busy ? (
          <Button
            variant="secondary"
            size="sm"
            icon="x"
            onClick={() => {
              stop.current = true;
            }}
          >
            {t("build.translate.stop")}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            icon="languages"
            disabled={unavailable || pending.length === 0}
            onClick={() => start(pending)}
          >
            {t("build.translate.all")}
          </Button>
        )}
        {total ? (
          <span class="muted small" role="status">
            {t("build.translate.total", { count: total })}
          </span>
        ) : null}
      </div>
      <ul class="build-locales">
        {locales.map((l) => {
          const s = statusOf(l.tag);
          const run = runs[l.tag];
          const has = Boolean(statuses[l.tag]);
          const pct = Math.round(s.coverage * 100);
          return (
            <li key={l.tag} class="build-locale">
              <div class="build-locale-info">
                <div class="build-locale-head">
                  <span class="build-locale-name" lang={l.tag}>
                    {l.name}
                  </span>
                  <span class="build-locale-state muted xs">
                    <span class="mono">{pct}%</span> ·{" "}
                    {run?.running
                      ? t("build.translate.running")
                      : current(s)
                        ? t("build.translate.current")
                        : t("build.translate.delta", { missing: s.missing, outdated: s.outdated })}
                  </span>
                </div>
                <div
                  class="build-meter"
                  role="progressbar"
                  aria-label={t("build.translate.coverage", { locale: l.name })}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                >
                  <span style={{ transform: `scaleX(${s.coverage})` }} />
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled={unavailable || busy || current(s)}
                onClick={() => start([l.tag])}
              >
                {t("build.translate.one")}
                <span class="visually-hidden"> {l.name}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon="trash"
                disabled={!has || busy}
                aria-label={t("build.translate.remove", { locale: l.name })}
                title={t("build.translate.remove", { locale: l.name })}
                onClick={() => setRemoving(l.tag)}
              />
              {run?.error ? (
                <p class="error-text small build-locale-error" role="alert">
                  {run.error}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {removing ? (
        <Dialog title={t("build.translate.removeTitle")} onClose={() => setRemoving(null)}>
          <div class="stack">
            <p>
              {t("build.translate.removeConfirm", {
                locale: LOCALES.find((l) => l.tag === removing)?.name ?? removing,
              })}
            </p>
            <div class="row">
              <Button variant="danger" onClick={() => remove(removing)}>
                {t("action.delete")}
              </Button>
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                {t("action.cancel")}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function Sharing({
  meta,
  onVisibility,
}: {
  meta: ScenarioMeta;
  onVisibility: (v: Visibility) => Promise<void>;
}) {
  const t = useT();
  const link = `${location.origin}/host/new?scenario=${encodeURIComponent(meta.id)}`;
  return (
    <div class="stack-sm">
      <Segmented
        label={t("build.share.visibility")}
        value={meta.visibility}
        options={(["private", "unlisted", "listed"] as const).map((v) => ({
          value: v,
          label: t(`build.visibility.${v}`),
        }))}
        onChange={(v) => {
          onVisibility(v);
        }}
      />
      <ul class="build-share-help small">
        {(["private", "unlisted", "listed"] as const).map((v) => (
          <li key={v} class={meta.visibility === v ? "current" : ""}>
            <strong>{t(`build.visibility.${v}`)}</strong> {t(`build.share.help.${v}`)}
          </li>
        ))}
      </ul>
      {meta.visibility !== "private" ? (
        <div class="stack-sm">
          <div class="label small">{t("build.share.link")}</div>
          <code class="build-link">{link}</code>
          <div class="row">
            <CopyButton text={link} />
          </div>
          {meta.publishedVersion ? null : <p class="muted small">{t("build.share.publishFirst")}</p>}
        </div>
      ) : null}
      {meta.visibility === "listed" ? (
        <p class="small">
          <Icon name={meta.approvedAt ? "badge-check" : "timer"} />{" "}
          {meta.approvedAt ? t("build.share.approved") : t("build.share.pending")}
        </p>
      ) : null}
    </div>
  );
}

export function ChecksPanel({
  meta,
  checks,
  onJump,
  statuses,
  totalStrings,
  model,
  ensureSaved,
  onStatus,
  onVisibility,
  onDelete,
}: {
  meta: ScenarioMeta;
  checks: Checks;
  onJump: (path: string) => void;
  statuses: Record<string, TranslationStatus>;
  totalStrings: number;
  model: string | null;
  ensureSaved: () => Promise<boolean>;
  onStatus: (locale: string, status: TranslationStatus | null) => void;
  onVisibility: (v: Visibility) => Promise<void>;
  onDelete: () => void;
}) {
  const t = useT();
  return (
    <div class="build-checks-body">
      <section class="build-panel" aria-labelledby="build-checks-h">
        <h2 id="build-checks-h" class="build-panel-title">
          {t("build.checks.title")}
        </h2>
        <FindingGroup
          title={t("build.checks.errors")}
          level="error"
          issues={checks.errors}
          onJump={onJump}
          emptyText={t("build.checks.noErrors")}
        />
        <FindingGroup
          title={t("build.checks.warnings")}
          level="warning"
          issues={checks.warnings}
          onJump={onJump}
          emptyText={t("build.checks.noWarnings")}
        />
        <FindingGroup
          title={t("build.checks.conventions")}
          level="convention"
          issues={checks.conventions}
          onJump={onJump}
          emptyText={t("build.checks.noConventions")}
        />
      </section>
      <section class="build-panel" aria-labelledby="build-play-h">
        <h2 id="build-play-h" class="build-panel-title">
          {t("build.play.title")}
        </h2>
        <p class="muted small">{t("build.play.help")}</p>
        <Playthrough play={checks.play} />
      </section>
      <section class="build-panel" aria-labelledby="build-tr-h">
        <h2 id="build-tr-h" class="build-panel-title">
          {t("build.translate.title")}
        </h2>
        <Translations
          id={meta.id}
          statuses={statuses}
          totalStrings={totalStrings}
          model={model}
          ensureSaved={ensureSaved}
          onStatus={onStatus}
        />
      </section>
      <section class="build-panel" id="build-sharing" aria-labelledby="build-share-h">
        <h2 id="build-share-h" class="build-panel-title">
          {t("build.share.title")}
        </h2>
        <Sharing meta={meta} onVisibility={onVisibility} />
      </section>
      <section class="build-panel danger" aria-labelledby="build-danger-h">
        <h2 id="build-danger-h" class="build-panel-title">
          {t("build.danger.title")}
        </h2>
        <p class="muted small">{t("build.danger.help")}</p>
        <div>
          <Button variant="danger" size="sm" icon="trash" onClick={onDelete}>
            {t("build.delete")}
          </Button>
        </div>
      </section>
    </div>
  );
}
